import { useState, useCallback, useRef, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { connectWithFacebook, verifyTokenExchange } from "@/app/api/social-connect";
import { toast as sonnerToast } from "sonner";
import { CONNECTION_TYPES } from "@/config";
import { createClient } from "@/auth/client";

interface FacebookConnectionState {
  isConnected: boolean;
  profile: {
    id: string;
    name: string;
    email?: string;
    picture_url?: string;
    connected_at: string;
    connectionId: string;
  } | null;
}

interface FacebookState {
  isLoadingFacebook: boolean;
  isConnecting: boolean;
  facebookError: string | null;
  connection: FacebookConnectionState;
  showSuccessEffect: boolean;
}

export function useFacebook() {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();

  const [state, setState] = useState<FacebookState>({
    isLoadingFacebook: true,
    isConnecting: false,
    facebookError: null,
    connection: {
      isConnected: false,
      profile: null
    },
    showSuccessEffect: false
  });

  const facebookFetchAttemptedForUserId = useRef<string | null>(null);

  // Update state helper
  const updateState = useCallback((updates: Partial<FacebookState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Update connection state helper
  const updateConnectionState = useCallback((updates: Partial<FacebookConnectionState>) => {
    setState(prev => ({ 
      ...prev, 
      connection: { ...prev.connection, ...updates }
    }));
  }, []);

  // Fetch Facebook connection status
  const fetchFacebookData = useCallback(async () => {
    if (!user?.id) return;

    try {
      updateState({ isLoadingFacebook: true, facebookError: null });

      const { data: facebookConnection, error: connectionError } = await supabase
        .from("connected_social_accounts")
        .select("*")
        .eq("user_id", user.id)
        .eq("connection_type", CONNECTION_TYPES.INSTAGRAM_VIA_FACEBOOK)
        .eq("status", "active")
        .single();

      if (connectionError || !facebookConnection) {
        updateConnectionState({
          isConnected: false,
          profile: null
        });
      } else {
        // Normalize picture URL to avoid expiring CDN signatures
        const rawPic: string | undefined = facebookConnection.profile_picture_url || undefined;
        let normalizedPic = rawPic;
        try {
          if (rawPic && typeof rawPic === 'string') {
            const isAlreadyProxied = rawPic.startsWith('/api/proxy/') || rawPic.includes('/api/proxy/');
            if (!isAlreadyProxied) {
              const u = new URL(rawPic, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
              const host = u.hostname;
              const isFbCdn = /fbcdn\.net$/i.test(host) || /fbsbx\.com$/i.test(host) || /lookaside\.facebook\.com$/i.test(host);
              if (isFbCdn) {
                normalizedPic = `/api/proxy/image?url=${encodeURIComponent(rawPic)}&v=${encodeURIComponent(facebookConnection.id)}`;
              }
            }
          }
        } catch {
          // leave normalizedPic as-is on parse error
        }
        updateConnectionState({
          isConnected: true,
          profile: {
            id: facebookConnection.account_id,
            name: facebookConnection.display_name,
            email: facebookConnection.username,
            picture_url: normalizedPic,
            connected_at: facebookConnection.connected_at,
            connectionId: facebookConnection.id
          }
        });
      }
    } catch (error: any) {
      console.error("Error fetching Facebook data:", error);
      updateState({ 
        facebookError: error.message || "Failed to load Facebook data",
        isLoadingFacebook: false 
      });
    } finally {
      updateState({ isLoadingFacebook: false });
    }
  }, [user, updateState, updateConnectionState, supabase]);

  // Connect with Facebook
  const connectFacebook = useCallback(async () => {
    if (!user) {
      console.error("User not authenticated");
      return;
    }

    try {
      updateState({ isConnecting: true, facebookError: null });
      await connectWithFacebook();
    } catch (error: any) {
      console.error("Error connecting Facebook:", error);
      updateState({ 
        facebookError: error.message || "Failed to connect Facebook",
        isConnecting: false 
      });
      
      sonnerToast.error(error.message || "Failed to connect to Facebook");
    }
  }, [user, updateState]);

  // Disconnect Facebook
  const disconnectFacebook = useCallback(async (connectionId?: string) => {
    if (!user?.id) {
      console.error("User not authenticated");
      return;
    }

    const targetConnectionId = connectionId || state.connection.profile?.connectionId;
    if (!targetConnectionId) {
      console.error("No connection ID available");
      return;
    }

    try {
      updateState({ isConnecting: true, facebookError: null });

      const { error } = await supabase
        .from("connected_social_accounts")
        .update({ status: "disconnected" })
        .eq("id", targetConnectionId)
        .eq("user_id", user.id);

      if (error) {
        throw new Error(error.message);
      }

      // Also disconnect any associated Instagram accounts
      await supabase
        .from("ig_via_fb_connected_ig_accounts")
        .update({ status: "disconnected" })
        .eq("connected_social_account_id", targetConnectionId)
        .eq("user_id", user.id);

      updateConnectionState({
        isConnected: false,
        profile: null
      });

      sonnerToast.success("Facebook account has been disconnected.");
      router.refresh();
    } catch (error: any) {
      console.error("Error disconnecting Facebook:", error);
      updateState({ facebookError: error.message || "Failed to disconnect Facebook" });
      sonnerToast.error(error.message || "Failed to disconnect Facebook");
    } finally {
      updateState({ isConnecting: false });
    }
  }, [user, state.connection.profile, updateState, updateConnectionState, router, supabase]);

  // Handle token exchange on redirect
  const handleTokenExchange = useCallback(async () => {
    if (!searchParams) return;
    
    const code = searchParams.get("code");
    const state_param = searchParams.get("state");
    const error = searchParams.get("error");
    const status = searchParams.get("status");
    const connection = searchParams.get("connection");

    if (error) {
      console.error("OAuth error:", error);
      updateState({ facebookError: `Authentication failed: ${error}` });
      return;
    }

    // Handle Facebook OAuth success redirect
    if (status === "success" && connection === "facebook" && user) {
      try {
        updateState({ isConnecting: true });

        const result = await verifyTokenExchange();

        if (result.success && result.instagramViaFacebookConnected) {
          updateState({ showSuccessEffect: true });
          sonnerToast.success("Facebook connected successfully!");

          // Fetch updated data
          await fetchFacebookData();

          // Clean up URL
          router.replace("/account");
        } else {
          throw new Error(result.error || "Facebook connection failed");
        }
      } catch (error: any) {
        console.error("Facebook connection error:", error);
        updateState({ facebookError: error.message || "Failed to complete Facebook connection" });
        sonnerToast.error(error.message || "Failed to complete Facebook connection");
      } finally {
        updateState({ isConnecting: false });
      }
    }

    // Handle regular OAuth redirect (with code/state)
    if (code && state_param && user) {
      try {
        updateState({ isConnecting: true });

        const result = await verifyTokenExchange();

        if (result.success) {
          updateState({ showSuccessEffect: true });
          sonnerToast.success("Facebook connected successfully!");

          // Fetch updated data
          await fetchFacebookData();

          // Clean up URL
          router.replace("/account");
        } else {
          throw new Error(result.error || "Token exchange failed");
        }
      } catch (error: any) {
        console.error("Token exchange error:", error);
        updateState({ facebookError: error.message || "Failed to complete authentication" });
        sonnerToast.error(error.message || "Failed to complete Facebook connection");
      } finally {
        updateState({ isConnecting: false });
      }
    }
  }, [searchParams, user, updateState, router, fetchFacebookData]);

  // Effects
  useEffect(() => {
    if (!isLoading && user?.id && facebookFetchAttemptedForUserId.current !== user.id) {
      facebookFetchAttemptedForUserId.current = user.id;
      fetchFacebookData();
      handleTokenExchange();
    }
  }, [user, isLoading, fetchFacebookData, handleTokenExchange]);

  return {
    // State
    ...state,
    
    // Actions
    connectFacebook,
    disconnectFacebook,
    refreshData: fetchFacebookData
  };
} 